import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminDashboardSummary } from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";

/**
 * `/admin` — équivalent de apps/web/app/(admin)/admin/page.tsx. Protégée par
 * `AdminRouteGuard` (router.tsx), pas ici. Porté de Server Component (fetch
 * direct côté serveur avec le cookie de session) vers `useQuery` (fetch côté
 * client, cookie httpOnly attaché par `credentials: "include"`).
 */
export function AdminDashboardPage() {
  const { data: summary, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "dashboard-summary"],
    queryFn: () => getAdminDashboardSummary(),
  });

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Tableau de bord</h1>
      <p className="mt-1 text-sm text-muted">Vue d&apos;ensemble de l&apos;activité VVS.</p>

      {isLoading && <p className="mt-6 text-sm text-muted">Chargement…</p>}

      {isError && (
        <p className="mt-6 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(error)}
        </p>
      )}

      {summary && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard label="Commandes aujourd'hui" value={summary.ordersToday.toString()} />
          <KpiCard label="Commandes cette semaine" value={summary.ordersThisWeek.toString()} />
          <KpiCard
            label="Chiffre d'affaires du jour"
            value={summary.revenueTodayMinor.formatted ?? `${summary.revenueTodayMinor.amountMinor}`}
          />
          <KpiCard
            label="En attente de confirmation"
            value={summary.pendingConfirmationCount.toString()}
            href="/admin/commandes"
            highlight={summary.pendingConfirmationCount > 0}
          />
          <KpiCard
            label="Alertes stock bas"
            value={summary.lowStockCount.toString()}
            href="/admin/stock"
            highlight={summary.lowStockCount > 0}
          />
        </div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  href,
  highlight,
}: {
  label: string;
  value: string;
  href?: string;
  highlight?: boolean;
}) {
  const content = (
    <div
      className={[
        "flex h-full flex-col justify-between rounded-sm border bg-white p-5 shadow-sm transition-colors",
        highlight ? "border-gold" : "border-line",
        href ? "hover:border-navy" : "",
      ].join(" ")}
    >
      <p className="text-sm font-medium text-muted">{label}</p>
      <p className="mt-3 font-display text-3xl font-semibold text-ink">{value}</p>
      {href && <p className="mt-2 text-sm font-medium text-navy">Voir le détail →</p>}
    </div>
  );

  if (!href) return content;

  return (
    <Link to={href} className="block">
      {content}
    </Link>
  );
}
